import type { LevelDef } from './types';

/**
 * Day 8 — Friday. Eight days since the park. Halfway home, the power goes out
 * across the whole district: every streetlight, every window. Only her phone,
 * very little battery, and a few candles on doorsteps. Mom stops answering.
 */
export const night7: LevelDef = {
  id: 'night7',
  night: 8,
  title: 'Blackout',
  subtitle: 'Friday · 00:31',
  look: 'snow',
  card: ['Eight days since the park.', 'Your phone didn\'t charge at work.', 'The old town: narrow lanes, the short way home.'],
  gps: 'none',
  outfit: 'work',
  clockStart: 31,
  map: [
    '#########################', // 0
    '#########H###############', // 1
    '#########.###############', // 2
    '#########.B..############', // 3
    '############.#####.######', // 4
    '############k#####.A#####', // 5
    '############.#####.######', // 6
    '###.....k.......L.....###', // 7
    '###.#################.###', // 8
    '###.#################.###', // 9
    '###.#################.###', // 10
    '###L#################.C##', // 11
    '###.#################k###', // 12
    '###.#################.###', // 13
    '###.#################.###', // 14
    '###.k.....###########.###', // 15
    '#########.#####.....k.###', // 16
    '#########.#####.#########', // 17
    '#########k#####.#########', // 18
    '#########.#####k#########', // 19
    '#########.##.##.#########', // 20
    '#########.##.##.#########', // 21
    '#####...k...k...L...#####', // 22
    '#####.######.######.#####', // 23
    '#####.######.######.#####', // 24
    '#####.######.######k#####', // 25
    '#####.######L....k..#####', // 26
    '#####k######.######.#####', // 27
    '#####.######.######.#####', // 28
    '#####.######.######.#####', // 29
    '#####.######.######.#####', // 30
    '###......L.........L..###', // 31
    '###.###.#########.###.###', // 32
    '###.###.#########.###.###', // 33
    '###.###.#########.###.###', // 34
    '###.###.#########.###.###', // 35
    '###.###........V..###.###', // 36
    '###L########.########.###', // 37
    '###.########.########L###', // 38
    '###.########L########.###', // 39
    '###.########.########.###', // 40
    '###.########.########.###', // 41
    '###.########.########.###', // 42
    '###...L...........V...###', // 43
    '############.############', // 44
    '############.############', // 45
    '############L############', // 46
    '############.############', // 47
    '############.############', // 48
    '############S############', // 49
    '############.############', // 50
    '############P############', // 51
  ],
  zones: [
    { r0: 31, zone: 1 },
    { r0: 15, r1: 30, zone: 2 },
    { r1: 14, zone: 3 },
  ],
  battery: 22,
  pace: { tension: 0.6, patience: 760 },
  theme: {
    sky: '#04060a',
    fog: { color: '#07090e', density: 0.032 },
    hemi: { sky: '#34405a', ground: '#09090a', intensity: 0.6 },
    ambient: 0.42,
    moon: 0.35,
    walls: ['#7a6f62', '#6d665c', '#86796a', '#5f5a52', '#7f7466'],
    roofs: ['#2a2622', '#332c26', '#26231f'],
  },
  stranger: { walkMax: 3.0, chaseSpeed: 3.12, gapMax: 31, spawnDelay: 5 },
  caughtHint: (ctx) => (ctx.said('blackout') ? 'In the blackout, her phone is the only light for blocks. Darkness hides her too.' : null),
  lines: {
    intro: 'Old town. The lanes are narrow but it\'s the quick way.',
    firstSight: 'There. At the end of the lane. It\'s him.',
    blackout: 'The lights… all of them. Everything\'s gone dark.',
    candles: 'Candles. Someone put a candle out on their step.',
    momGone: 'Not delivered. Mom… pick up. Please.',
    deadend: 'Dead end. I can\'t see a thing back here.',
    konbini: 'Even the konbini is dark.',
    wrongBuilding: 'Not mine. Wrong door.',
    homeStretch: 'Home. Keys, keys, keys.',
  },
  mom: {
    first: { node: 'start', at: 6 },
    script: {
      start: {
        text: 'Are you on your way? Text me when you pass the shrine.',
        options: [
          { text: 'On my way', end: 'Ok. Love you.' },
          { text: 'My phone is nearly dead', next: 'dead' },
        ],
      },
      dead: {
        text: 'Then put it away and walk. Save it for emergencies.',
        options: [
          { text: 'Ok', end: "Text me only if something's wrong." },
          { text: "I'm scared", end: "I know. You're nearly here." },
        ],
      },
      outage: {
        text: 'The power just went out here too!! Where are you??',
        options: [
          { text: 'Old town, it\'s pitch black', next: 'dark' },
          { text: 'Almost home', next: 'dark' },
        ],
      },
      dark: {
        text: 'Use your phone light. My battery is at 3%, I',
        last: true,
        options: [
          { text: 'Mom?', end: '...' },
          { text: 'Mom??', end: '...' },
        ],
      },
    },
  },
  onEnterCell(ctx, cell) {
    const key = `${cell.c},${cell.r}`;
    if (key === '17,26' || key === '18,26') {
      if (!ctx.said('deadend')) {
        ctx.spike(0.06);
        ctx.sayOnce('deadend');
      }
    }
    if (key === '21,11') ctx.sayOnce('konbini');
    if (cell.r === 3) ctx.sayOnce('homeStretch');
  },
  onZoneChange(ctx, zone) {
    if (zone === 2 && !ctx.said('blackout')) {
      ctx.blackout();
      ctx.sayLater('blackout', 1.2);
      ctx.momLater('outage', 5);
      ctx.sayLater('candles', 16);
    }
    if (zone === 3 && !ctx.said('momGone')) {
      ctx.silenceMom();
      ctx.sayLater('momGone', 3);
    }
  },
};
